import { Button } from "@bcc/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bcc/components/ui/dialog";
import { Input } from "@bcc/components/ui/input";
import { Label } from "@bcc/components/ui/label";
import { useToast } from "@bcc/hooks/use-toast";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { setShopDomainModalTestId } from "./shop-domain-modal.utils";

// Mock API call - simulates /api/onboarding/connect
// TODO: Replace with real API call when backend is ready
async function mockConnectApi(
  platform: string,
  shopDomain: string,
): Promise<{ success: boolean; message: string }> {
  // Simulate network delay (2 seconds)
  await new Promise((resolve) => setTimeout(resolve, 2000));

  // Return mock successful response
  return {
    success: true,
    message: `Successfully connected ${platform} store: ${shopDomain}`,
  };
}

interface ShopDomainModalProps {
  platform: "shopify" | "woocommerce" | null;
  isOpen: boolean;
  onClose: () => void;
  testId?: string;
}

export function ShopDomainModal({
  platform,
  isOpen,
  onClose,
  testId,
}: ShopDomainModalProps) {
  const { t } = useTranslation();
  const [shopDomain, setShopDomain] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleConnect = async () => {
    if (!shopDomain.trim()) {
      setError(t("onboarding.shopDomainModal.errors.enterDomain"));
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      // Use mock API call instead of real fetch
      const data = await mockConnectApi(platform || "", shopDomain);

      if (!data.success) {
        throw new Error("Failed to connect");
      }

      toast({
        title: t("onboarding.shopDomainModal.toasts.connectionSuccessful"),
        description: t("onboarding.shopDomainModal.toasts.letsMapProducts"),
      });

      // Store the platform in localStorage for later use
      localStorage.setItem("tempConnectedPlatform", platform || "");

      // Navigate to map products
      navigate("/onboarding/map-products");
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("onboarding.shopDomainModal.errors.couldntConnect"),
      );
      toast({
        title: t("onboarding.shopDomainModal.toasts.connectionFailed"),
        description: t("onboarding.shopDomainModal.toasts.tryAgainOrContact"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        testId={testId ?? setShopDomainModalTestId("content")}
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>
            {t("onboarding.shopDomainModal.connectPlatform", {
              platform: platform === "shopify" ? "Shopify" : "WooCommerce",
            })}
          </DialogTitle>
          <DialogDescription>
            {t("onboarding.shopDomainModal.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="shop-domain">
              {t("onboarding.shopDomainModal.shopDomain")}
            </Label>
            <Input
              id="shop-domain"
              placeholder={
                platform === "shopify" ? "your-store-name" : "yourdomain.com"
              }
              value={shopDomain}
              onChange={(e) => {
                setShopDomain(e.target.value);
                setError("");
              }}
              disabled={isLoading}
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        </div>
        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleConnect} disabled={isLoading}>
            {isLoading
              ? t("onboarding.shopDomainModal.connecting")
              : t("onboarding.shopDomainModal.connect")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
